import { useEffect, useState } from 'react';

import {
  useGetMomentsApiSessionReadinessQuery,
  useGetMomentsFeatureFlagsForSelfQuery,
} from '@/store/api/momentsApi';
import { useAppSelector } from '@/store/hooks';

export const MEMBER_REFLECTION_KEEP_ACTIONS = 'MEMBER_REFLECTION_KEEP_ACTIONS';
const MAX_FLAG_AGE_MS = 60_000;
const isSuccessfulPayload = (success: boolean, error: boolean) =>
  success && !error;

// Whether this member is in the Keep cohort at all, independent of any
// reflection. Off until the member's flags load, so entry points to Keep never
// flash for members outside it.
export const useReflectionKeepFlag = () => {
  const { data } = useGetMomentsFeatureFlagsForSelfQuery();
  return Array.isArray(data?.flags)
    ? data.flags.includes(MEMBER_REFLECTION_KEEP_ACTIONS)
    : false;
};

export interface ReflectionKeepSource {
  id: string;
  ownerPersonId: string;
  profileSlug: string;
  revision: number;
}

// Availability only. Existing note reads/edits/deletes never use this gate.
export const useReflectionKeepCohort = (source?: ReflectionKeepSource) => {
  const generation = useAppSelector((state) => state.app.momentsGeneration);
  const readinessQuery = useGetMomentsApiSessionReadinessQuery(undefined, {
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true,
    refetchOnReconnect: true,
    skip: !source,
  });
  const readiness = readinessQuery.currentData;
  const eligible = Boolean(
    source?.id &&
    source.profileSlug === 'member-reflection' &&
    Number.isInteger(source.revision) &&
    source.revision > 0 &&
    readiness?.ready &&
    readiness.provisioned &&
    readiness.preconditionsMet &&
    readiness.capabilities.memberApi &&
    Array.isArray(readiness.roles) &&
    readiness.roles.includes('member') &&
    readiness.person?.id === source.ownerPersonId &&
    Number.isInteger(generation),
  );
  const query = useGetMomentsFeatureFlagsForSelfQuery(undefined, {
    pollingInterval: 30_000,
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true,
    refetchOnReconnect: true,
    skip: !eligible,
    skipPollingIfUnfocused: true,
  });
  const [clock, setClock] = useState(Date.now());
  const [visible, setVisible] = useState(
    document.visibilityState === 'visible',
  );
  const stamp = query.fulfilledTimeStamp;
  useEffect(() => {
    setClock(Date.now());
    if (!stamp) {
      return undefined;
    }
    const timer = window.setTimeout(
      () => setClock(Date.now()),
      Math.max(0, stamp + MAX_FLAG_AGE_MS - Date.now()) + 1,
    );
    return () => window.clearTimeout(timer);
  }, [stamp, generation]);
  useEffect(() => {
    const resume = () => {
      setClock(Date.now());
      setVisible(document.visibilityState === 'visible');
      if (document.visibilityState === 'visible' && eligible) {
        query.refetch().catch(() => undefined);
      }
    };
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('focus', resume);
    return () => {
      document.removeEventListener('visibilitychange', resume);
      window.removeEventListener('focus', resume);
    };
  }, [eligible, query.refetch]);
  const flags = query.currentData;
  const rolesMatch =
    Array.isArray(flags?.subject.roles) &&
    flags.subject.roles.includes('member') &&
    JSON.stringify([...flags.subject.roles].sort()) ===
      JSON.stringify([...(readiness?.roles ?? [])].sort());
  const fresh =
    typeof stamp === 'number' &&
    clock >= stamp &&
    clock - stamp < MAX_FLAG_AGE_MS;
  const enrolled = Boolean(
    eligible &&
    fresh &&
    isSuccessfulPayload(query.isSuccess, query.isError) &&
    !readinessQuery.isError &&
    flags?.source === 'moments' &&
    flags.subject.personId === readiness?.person?.id &&
    rolesMatch &&
    Array.isArray(flags.flags) &&
    flags.flags.includes(MEMBER_REFLECTION_KEEP_ACTIONS),
  );
  const enabled =
    enrolled && visible && !query.isFetching && !readinessQuery.isFetching;
  return {
    enabled,
    enrolled,
    generation,
    personId: readiness?.person?.id,
    isChecking: query.isFetching || readinessQuery.isFetching,
  };
};
